import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ToastrService} from 'ngx-toastr';
import {OpenGroundProductionJobService} from '../../open-ground-production-job.service';
import {ProductionJob} from '../../../pot/production-job';
import {CreateStockWithProductionJob} from '../../../pot/pot-jobs/create-stock-dialog/create-stock-with-production-job';

@Component({
  selector: 'app-printing-dialog',
  templateUrl: './create-open-ground-stock-dialog.component.html',
  styleUrls: ['./create-open-ground-stock-dialog.component.scss']
})
export class CreateOpenGroundStockDialogComponent {

  originalAmount: number;
  inputMaterialAmount: number;
  inputMaterialAmountTrashed: number;
  remarks: string;
  location: string;
  productionDate: Date;
  bookAutomaticallyFromStock: boolean;
  materialUnitSizeId: number;
  targetUnitSizeId: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductionJob,
    private dialogRef: MatDialogRef<CreateOpenGroundStockDialogComponent>,
    private openGroundProductionJobService: OpenGroundProductionJobService,
    private toastrService: ToastrService
  ) {
    const reserved = data.totalReservedAmount ?? 0;

    this.originalAmount = data.orderedAmount ?? 0;
    this.remarks = data.remarks;

    if (this.data.hasReservedStock) {
      this.inputMaterialAmount = reserved;
      this.inputMaterialAmountTrashed = 0;
    } else {
      this.inputMaterialAmount = this.originalAmount;
      this.inputMaterialAmountTrashed = 0;
    }

    this.bookAutomaticallyFromStock = data.hasReservedStock;

    this.location = localStorage.getItem('last-open-ground-location') || '';
    const storedDate = localStorage.getItem('last-production-date');
    this.productionDate = storedDate ? new Date(storedDate) : new Date();

    this.targetUnitSizeId = data.targetUnitSizeId;
    this.materialUnitSizeId = data.materialUnitSizeId;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private normalizeToZero(value: any): number {
    const num = Number(value);
    if (isNaN(num) || value === null || value === undefined || value === '') {
      return 0;
    }
    return num;
  }

  async onSubmitClick() {
    const original = this.normalizeToZero(this.originalAmount);
    const used = this.normalizeToZero(this.inputMaterialAmount);
    const trashed = this.normalizeToZero(this.inputMaterialAmountTrashed);

    const newStockEntity = new CreateStockWithProductionJob(
      original,
      this.location,
      this.remarks,
      this.data.id,
      this.productionDate,
      used,
      trashed,
      this.bookAutomaticallyFromStock,
      this.targetUnitSizeId,
      this.materialUnitSizeId
    );

    try {
      await this.openGroundProductionJobService.createNewStockEntity(newStockEntity).toPromise();
      this.toastrService.success('Nieuwe voorraad succesvol aangemaakt!');
      this.dialogRef.close({ success: true });
    } catch (e) {
      this.toastrService.error('Er is iets mis gegaan!');
      console.error(e);
    }
  }

  isDisabled() {
    const original = this.normalizeToZero(this.originalAmount);

    if (original <= 0 || !this.location || this.location.trim().length === 0) {
      return true;
    }

    if (this.data.hasReservedStock) {
      const reserved = this.data.totalReservedAmount ?? 0;
      const used = this.normalizeToZero(this.inputMaterialAmount);
      const trashed = this.normalizeToZero(this.inputMaterialAmountTrashed);

      if (used + trashed > reserved) {
        return true;
      }
    }

    return false;
  }

  saveLocationToLocalStorage(location: string) {
    if (location) {
      localStorage.setItem('last-open-ground-location', location);
    }
  }

  saveDateToLocalStorage(date: Date) {
    if (date) {
      localStorage.setItem('last-production-date', date.toISOString());
    }
  }

  onInputAmountUsedChanged(value: number | null) {
    let used = this.normalizeToZero(value);

    if (this.data.hasReservedStock) {
      const reserved = this.data.totalReservedAmount ?? 0;

      if (used < 0) {
        used = 0;
      }
      if (used > reserved) {
        used = reserved;
      }

      this.inputMaterialAmount = used;
      this.inputMaterialAmountTrashed = reserved - used;
    } else {
      if (used < 0) {
        used = 0;
      }
      this.inputMaterialAmount = used;
    }
  }

  onTrashedInput(event: Event) {
    const reserved = this.data.totalReservedAmount ?? 0;
    const inputEl = event.target as HTMLInputElement;

    let trashed = this.normalizeToZero(inputEl.value);

    if (this.data.hasReservedStock) {
      if (trashed < 0) {
        trashed = 0;
      }

      const used = this.normalizeToZero(this.inputMaterialAmount);
      const maxTrashedGivenUsed = Math.max(0, reserved - used);

      if (trashed > maxTrashedGivenUsed) {
        trashed = maxTrashedGivenUsed;
      }
    } else {
      if (trashed < 0) {
        trashed = 0;
      }
    }

    this.inputMaterialAmountTrashed = trashed;

    inputEl.value = String(trashed);
  }

  onOriginalAmountChanged(value: number | null) {
    this.originalAmount = this.normalizeToZero(value);
  }
}
